import React from "react";
import cx from "classnames";

import { isSuspendedMarketStatus } from "@/utilities/sharedBettingUtilities";
import { CompetitorAvatar } from "@/features/betting/components/CompetitorAvatar";
import { isRacingCompetitor } from "@/hooks/firestore/betting/useRacingEvents";
import { getOutcomeCompetitor, type OutcomeLayoutProps } from "./outcomeLayout";

import type { OutcomeType } from "@/hooks/firestore/models/outcome";

import * as styles from "./LiftedTeamNames.module.scss";

type LiftedTeamNamesProps = Pick<
  OutcomeLayoutProps,
  "outcomes" | "competitors" | "mainMarket"
> & { showLogos?: boolean };

// handicap lines label their outcomes "+1.5" / "-1.5", which says nothing about
// who they belong to. this lifts the two team names above the outcomes instead
const LiftedTeamNames = ({
  outcomes,
  competitors,
  mainMarket,
  showLogos = true,
}: LiftedTeamNamesProps) => {
  const allOutcomes = Object.values(outcomes);
  const isSuspended =
    allOutcomes.length > 0 &&
    allOutcomes.every((outcome) =>
      isSuspendedMarketStatus(outcome.market?.status),
    );

  const renderSide = (types: (OutcomeType | undefined | null)[]) => {
    const outcome = Object.values(outcomes).find((outcome) =>
      types.includes(outcome.type),
    );
    const competitor = getOutcomeCompetitor(competitors, outcome, mainMarket);

    if (!competitor || !outcome) return null;

    // this layout is betting-only, but the shared lookup's type also covers racing
    const avatar = isRacingCompetitor(competitor) ? undefined : competitor;

    return (
      <div
        className={cx(styles.franchiseLogo, {
          [styles.left]: outcome.type?.includes("HOME"),
        })}
      >
        <span className={styles.franchiseName}>{competitor.name}</span>
        {showLogos && (
          <CompetitorAvatar className={styles.avatar} competitor={avatar} />
        )}
      </div>
    );
  };

  const home = renderSide(["FRANCHISE_HOME", "PLAYER_HOME"]);
  const away = renderSide(["FRANCHISE_AWAY", "PLAYER_AWAY"]);

  return (
    <div
      className={cx(styles.liftedTeamNames, {
        [styles.darken]: isSuspended,
        // a lone side should fill the pill, not leave an empty column
        [styles.single]: !home || !away,
        [styles.hasLogos]: showLogos,
      })}
    >
      {home}
      {away}
    </div>
  );
};

export { LiftedTeamNames };
